import { useState, useEffect, useRef, useMemo } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { GET_RAW_ASSET_DETAILS_BY_ID, GET_RECONCILE_RESULT_BY_ID } from '../../../components/graphql/queries'
import { AMEND_ASSET_STATUS, SAVE_UPDATED_FIELDS } from '../../../components/graphql/mutations'
import { transformAssetData, formInputSessionData, getEditableFieldsForAssetType } from '../utils/formatting'
import { DEFAULT_INPUT_WRAP_DATA, TRADER_INPUT_FIELDS_MAP } from '../../../utility/constant'
import type { AssetDetailsData, InputAssetData, RiskCapTraderSharedAssetDetailsHookProps, NoteType } from '../../../utility/types'
import { getUpdatedFields, getUpdatedSummaryFields, amendFRAssetStatus } from '../utils/assetBucketsHelpers'
import { useUserEmail } from '../../../hooks/useUserEmail'
import { handleRecordRemove } from '../../../components/records-table/shared'
import { DATACHECKINGFIELDS, DATAVALIDATIONFIELDS } from '../utils/constant'
import type { ExecSchemeOption, FrOption, ReconcileResultData } from '../utils/types'
import { useQueryWithExtendedTime } from '../../../hooks/useQueryWithExtendedTime'

export const useAssetDetails = ({
  batchId,
  batchUniqueId,
  assetType = 'EQTY',
  setMessage,
  setMessageColor,
  status: approvalStatus,
  onClose,
  basketOverall = false,
  updateError,
  reconcileIndicator = 0,
  reconcileCount = 0
}: RiskCapTraderSharedAssetDetailsHookProps) => {
  const [records, setRecords] = useState<AssetDetailsData[]>([])
  const originalDataRef = useRef<{
    originalRecords: AssetDetailsData[]
    originalInputData: InputAssetData
    originalFRCode: string | null
    originalFRId: number | null
    originalExecSchemeId: number | null
    originalExecScheme: string | null
  } | null>(null)
  const initialRecord = records.length > 0 ? records[0] : null
  const initialFormData = TRADER_INPUT_FIELDS_MAP(assetType).reduce<InputAssetData>((formData, field) => {
    if (initialRecord && field.name in initialRecord) {
      formData[field.name as keyof InputAssetData] = initialRecord[field.name as keyof AssetDetailsData] as string
    } else {
      formData[field.name as keyof InputAssetData] = ''
    }
    return formData
  }, DEFAULT_INPUT_WRAP_DATA)
  const [inputData, setInputData] = useState<InputAssetData>(initialFormData)
  const [riskOverall, setRiskOverall] = useState<boolean>(basketOverall)
  const [isDataReady, setIsDataReady] = useState(false)
  const [dataChanged, setDataChanged] = useState<boolean>(false)
  const [ableToProceed, setAbleToProceed] = useState<boolean>(false)
  const [changeType, setChangeType] = useState<'data' | 'note' | 'none'>('none')
  const [isMutating, setIsMutating] = useState<boolean>(false)
  const [isSecondaryMutating, setIsSecondaryMutating] = useState<boolean>(false)
  const [hasFlag, setHasFlag] = useState<boolean>(false)
  const [isConfirmationDialogActive, setIsConfirmationDialogActive] = useState<boolean>(false)
  const [isRemoveRecordModalOpen, setIsRemoveRecordModalOpen] = useState(false)
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const [hasDataChanges, setHasDataChanges] = useState<boolean>(false)
  const [FRDropDownOptions, setFRDropDownOptions] = useState<FrOption[]>([])
  const [recalcMessage, setRecalcMessage] = useState<string | null>(null)
  const [recalcMessageColor, setRecalcMessageColor] = useState<'red' | 'green' | 'yellow'>('green')
  const editableField = getEditableFieldsForAssetType(assetType)
  const { userEmail, isReady: isUserEmailReady } = useUserEmail()
  const [traderNote, setTraderNote] = useState<string>('')
  const [displayingNotes, setDisplayingNotes] = useState<NoteType[]>([])
  const [selectedFRCode, setSelectedFRCode] = useState<string | null>(null)
  const [selectedFRID, setSelectedFRID] = useState<number | null>(null)
  const [reconciliationResultData, setReconciliationResultData] = useState<ReconcileResultData | null>(null)
  const [execSchemeOptions, setExecSchemeOptions] = useState<ExecSchemeOption[]>([])
  const [selectedExecSchemeId, setSelectedExecSchemeId] = useState<number | null>(null)
  const [selectedExecScheme, setSelectedExecScheme] = useState<string | null>(null)
  const [portfolioReadOnly, setPortfolioReadOnly] = useState<boolean>(false)
  const [reconcileStatus, setReconcileStatus] = useState<string | null>(null)
  const [holidayValidationMode, setHolidayValidationMode] = useState<string>('')
  const [dateException, setDateException] = useState<boolean>(false)
  const holidayAsWarning = holidayValidationMode === 'HolidayAsWarning'
  const [basketInstrumentCurrency, setBasketInstrumentCurrency] = useState<string>('')
  const [basketInstrumentIssue, setBasketInstrumentIssue] = useState<string>('')

  // Mutations
  const [saveUpdatedFieldsMutation] = useMutation(SAVE_UPDATED_FIELDS)
  const [amendFRAssetStatusMutation] = useMutation(AMEND_ASSET_STATUS)
  // const [notifyCounterpartyMutation] = useMutation(NOTIFY_COUNTERPARTY)

  // Query - skip until userEmail is available
  const { data: assetDetailsData, loading, error, refetch: assetDetailsRefetch, QueryWithExtendedTimeComponent } = useQueryWithExtendedTime({
    query: GET_RAW_ASSET_DETAILS_BY_ID,
    options: {
      skip: !isUserEmailReady || !batchUniqueId, // Skip query if userEmail is not ready or batchUniqueId is missing
      variables: {
        batch_unique_id: batchUniqueId,
        note_user: userEmail ?? '', // Provide empty string as fallback (though skip should prevent this)
        user_running: userEmail ?? '',
        return_result_option: 'assetSummary|assetDetail|userNotes|availFRs|availOEMSExecScheme'
      }
    }
  })

  const [fetchReconciliationData, { data: reconciliationData, loading: loadingRecon, error: errorRecon }] = useLazyQuery(GET_RECONCILE_RESULT_BY_ID, {
    variables: { batchId },
    fetchPolicy: 'cache-and-network'
  })

  const handleRecordRemoval = (uniqueBatchId: number) => {
    setIndexToRemove(uniqueBatchId)
    setIsRemoveRecordModalOpen(true)
  }

  const confirmRecordRemoval = async () => {
    if (indexToRemove !== null) {
      handleRecordRemove<AssetDetailsData>(setRecords, indexToRemove)
      setIndexToRemove(null)
    }
    setIsRemoveRecordModalOpen(false)
  }

  const getPrimaryLabel = useMemo(() => {
    const computeLabel = (): string => {
      if (reconcileIndicator === 9) {
        return 'Reject' // Only allow reject when reconcileIndicator is 9
      }

      // In risk pre-approved states where reject is allowed, keep label as Reject regardless of notes
      if ((approvalStatus === 'RISK-PREAPPROVED' || approvalStatus === 'RISK-PREAPPROVED-EXCEPTION') && reconcileIndicator !== 5 && reconcileIndicator !== 8) {
        return 'Reject'
      }

      // For other reconcileIndicator values, check isDataReady and portfolioReadOnly
      if (!isDataReady && !portfolioReadOnly) return changeType === 'note' ? 'Save Note' : 'Link Intercompany Loan'

      // If there are pending data edits (not just notes), always prefer Save & Recalc label
      if (dataChanged && changeType === 'data') return 'Save & Recalc'

      // If previously rejected, allow approve again (read-only state should not show Save & Recalc)
      if (approvalStatus === 'TRADER-REJECTED') {
        return riskOverall ? 'Proceed' : 'Proceed with exceptions'
      }

      // When risk has pre-approved, allow trader to proceed accordingly
      if (approvalStatus === 'RISK-PREAPPROVED' || approvalStatus === 'RISK-PREAPPROVED-EXCEPTION') {
        if (reconcileIndicator === 5) return 'Proceed'
        if (reconcileIndicator === 8) return 'Proceed with exceptions'
      }

      // Special case: No reconciliation flow. Under ASSET-READY allow proceed,
      // but if there are pending data edits, show Save & Recalc instead
      if (reconcileIndicator === 0 && approvalStatus === 'ASSET-READY') {
        if (dataChanged && changeType === 'data') return 'Save & Recalc'
        return riskOverall ? 'Proceed' : 'Proceed with exceptions'
      }
      if (!ableToProceed) {
        if (changeType === 'data') return 'Save & Recalc'
        if (changeType === 'note') return 'Save Note'
        return 'Save & Recalc'
      }

      // Default case (reconcileIndicator === 1 or other values)
      return riskOverall ? 'Proceed' : 'Proceed with exceptions'
    }

    const label = computeLabel()
    // A holiday/non-business-day date approved as an exception always requires a note
    return dateException && label === 'Proceed' ? 'Proceed with exceptions' : label
  }, [ableToProceed, changeType, isDataReady, reconcileIndicator, riskOverall, portfolioReadOnly, approvalStatus, dataChanged, dateException])

  const shouldShowPreApproveButtons = useMemo(() => {
    // Never show pre-approve when reconcileIndicator is 0
    if (reconcileIndicator === 0) return false
    // Allow pre-approve buttons after TRADER-REJECTED or RISK-REJECTED
    const isAfterRejection = approvalStatus === 'TRADER-REJECTED' || approvalStatus === 'RISK-REJECTED'
    // Basic case: allow when Waiting-Recon (1) and status is ASSET-READY
    const isBasicWaitingRecon = reconcileIndicator === 1 && approvalStatus === 'ASSET-READY'
    // Only show pre-approve for reconcileIndicator === 1 even after rejections
    const result = reconcileIndicator === 1 && ((portfolioReadOnly || isBasicWaitingRecon) || isAfterRejection)
    return result
  }, [reconcileIndicator, portfolioReadOnly, approvalStatus])

  const shouldShowPreApproveException = useMemo(() => {
    return shouldShowPreApproveButtons && !riskOverall
  }, [shouldShowPreApproveButtons, riskOverall])

  const shouldShowRejectButton = useMemo(() => {
    // Show reject button for RISK-PREAPPROVED states or when reconcileIndicator is 9
    // Exclude RISK-REJECTED when we want to show pre-approve buttons
    return ((approvalStatus === 'RISK-PREAPPROVED' || approvalStatus === 'RISK-PREAPPROVED-EXCEPTION') && reconcileIndicator !== 5 && reconcileIndicator !== 8) || reconcileIndicator === 9
  }, [approvalStatus, reconcileIndicator])

  const hasRecordDataChanged = (): boolean => {
    if (!originalDataRef.current || records.length === 0) return false

    const originalRecordsMap = new Map(
      originalDataRef.current.originalRecords.map(record => [record.UniqueBatchId, record])
    )
    const currentRecords = records.filter((record) => !record.isRemoved)

    if (currentRecords.length !== originalRecordsMap.size) {
      return true
    }

    return records.some(record => {
      const originalRecord = originalRecordsMap.get(record.UniqueBatchId)
      if (!originalRecord) { // if UniqueBatchId does not exist, records changed
        return true
      }
      return editableField.some(field => {
        const currentValue = record[field as keyof AssetDetailsData]
        const originalValue = originalRecord[field as keyof AssetDetailsData]
        return currentValue !== originalValue
      })
    })
  }

  const hasInputDataChanged = (): boolean => {
    if (!originalDataRef.current || !inputData) return false

    return DATACHECKINGFIELDS.some((field) => inputData[field as keyof InputAssetData] !== originalDataRef.current?.originalInputData[field as keyof InputAssetData])
  }

  const hasFundingRequestChanged = (): boolean => {
    if (!originalDataRef.current) return false
    if (!selectedFRCode && !originalDataRef.current.originalFRCode) return false
    return selectedFRCode !== originalDataRef.current.originalFRCode
  }

  const hasExecutionSchemeChanged = (): boolean => {
    if (!originalDataRef.current) return false
    if (!selectedExecSchemeId && !originalDataRef.current.originalExecSchemeId) return false
    return selectedExecSchemeId !== originalDataRef.current.originalExecSchemeId
  }

  const handleSaveUpdatedFields = async () => {
    if (!userEmail) {
      return { message: 'User email is not available. Please refresh the page.', color: 'red' as const }
    }

    const originalRecords = originalDataRef.current?.originalRecords ?? []
    const originalInputData = originalDataRef.current?.originalInputData ?? initialFormData

    const updatedFields = getUpdatedFields(records, originalRecords, editableField)
    const updatedFieldsSummaryInputs = getUpdatedSummaryFields(inputData, originalInputData)
    let message = ''
    let color = 'red'

    if (dataChanged) {
      try {
        const response = await saveUpdatedFieldsMutation({
          variables: {
            updated_fields: updatedFields,
            updated_fields_summary: updatedFieldsSummaryInputs,
            batch_unique_id: batchUniqueId,
            note: traderNote,
            updated_by: userEmail,
            funding_request_id: originalDataRef.current?.originalFRId === selectedFRID ? null : selectedFRID,
            execution_scheme_id: originalDataRef.current?.originalExecSchemeId === selectedExecSchemeId ? null : selectedExecSchemeId
          }
        })
        if (response?.data?.storeSavedFRDataToDatabase) {
          const { status, message: responseMessage, detailsData, summaryData, noteData, availFRs, availOEMSExecScheme } =
            response.data.storeSavedFRDataToDatabase

          if (status === 'success') {
            const transformedData = transformAssetData(detailsData)
            const inputFieldData = formInputSessionData(summaryData)
            setRiskOverall(summaryData.BasketOverall && summaryData.TransactionType === 'Deal')
            setRecords(transformedData)
            setInputData(inputFieldData)
            setDisplayingNotes(noteData)
            setTraderNote('')
            originalDataRef.current = {
              originalRecords: transformedData,
              originalInputData: inputFieldData,
              originalFRCode: availFRs.find((fr: FrOption) => fr.selectedFR)?.FundingRequestCode || null,
              originalFRId: availFRs.find((fr: FrOption) => fr.selectedFR)?.FundingRequestId || null,
              originalExecSchemeId: availOEMSExecScheme.find((scheme: ExecSchemeOption) => scheme.currSelectedExecScheme)?.OemsExecSchemeId || null,
              originalExecScheme: availOEMSExecScheme.find((scheme: ExecSchemeOption) => scheme.currSelectedExecScheme)?.ExecutionScheme || null
            }

            message = responseMessage || 'Fields updated successfully.'
            color = 'green'
          } else {
            message = responseMessage || 'Failed to update fields.'
            color = 'red'
          }
        } else {
          message = 'No response data received from the server.'
          color = 'red'
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.'
        message = `Error saving updated fields: ${errorMessage}`
        color = 'red'
      }
    } else {
      message = 'No fields have been updated.'
      color = 'red'
    }

    return { message, color }
  }

  const areAllFieldsFilled = (): boolean => {
    // Check records fields
    const recordsWithEmptyFields = records.filter((record) =>
      editableField.some((field) => record[field as keyof AssetDetailsData] === '' || record[field as keyof AssetDetailsData] == null)
    )

    if (recordsWithEmptyFields.length > 0) {
      console.log('areAllFieldsFilled - records with empty fields:', recordsWithEmptyFields)
      return false
    }

    // Check input data fields
    const emptyInputFields = DATAVALIDATIONFIELDS.filter((field) => {
      const value = inputData[field as keyof InputAssetData]
      return value === '' || value == null || (typeof value === 'string' && value.startsWith('1900'))
    })

    if (emptyInputFields.length > 0) {
      console.log('areAllFieldsFilled - empty input fields:', emptyInputFields)
      return false
    }

    console.log('areAllFieldsFilled - all fields are filled')
    return true
  }

  const handleDiscard = async (): Promise<void> => {
    setIsSecondaryMutating(true)
    if (isConfirmationDialogActive) {
      setIsConfirmationDialogActive(false)
      if (originalDataRef.current) {
        setRecords(originalDataRef.current.originalRecords)
        setInputData(originalDataRef.current.originalInputData)
        setSelectedFRCode(originalDataRef.current.originalFRCode)
        setSelectedFRID(originalDataRef.current.originalFRId)
        setSelectedExecSchemeId(originalDataRef.current.originalExecSchemeId)
        setSelectedExecScheme(originalDataRef.current.originalExecScheme)
      }
    }
    setTraderNote('')
    setIsSecondaryMutating(false)
    setRecalcMessage?.(null)
    onClose?.()
  }

  const handleSaveRecalc = async (): Promise<void> => {
    setIsMutating(true)
    const { message, color } = await handleSaveUpdatedFields()
    setRecalcMessage(message)
    setRecalcMessageColor(color as 'red' | 'green' | 'yellow')
    setIsMutating(false)
  }

  const handleSaveExit = async () => {
    setIsMutating(true)
    const { message, color } = await handleSaveUpdatedFields()
    setMessage?.(message)
    setMessageColor?.(color as 'red' | 'green' | 'yellow')
    setIsMutating(false)
    onClose?.()
  }

  const handleCloseIconClick = () => {
    if (dataChanged) {
      setIsConfirmationDialogActive(true)
    } else {
      onClose?.()
    }
    setRecalcMessage?.(null)
  }

  const handleRejectClick = async () => {
    if (!traderNote.trim()) {
      setRecalcMessage('Cannot reject without notes. Please leave a note.')
      setRecalcMessageColor('yellow')
      return
    }

    if (!userEmail) {
      setRecalcMessage('User email is not available. Please refresh the page.')
      setRecalcMessageColor('red')
      return
    }

    setIsSecondaryMutating(true)
    try {
      await amendFRAssetStatus({
        amendFRAssetStatusMutation,
        batchUniqueId,
        approvalStatus: 'TRADER-REJECTED',
        fundingRequestId: selectedFRID,
        processRequested: 'CapMarket',
        updatedBy: userEmail,
        note: traderNote,
        setMessage,
        setMessageColor
      })
      onClose?.()
    } catch (error) {
      setRecalcMessage?.('Failed to reject asset.')
      setRecalcMessageColor?.('red')
    } finally {
      setIsSecondaryMutating(false)
    }
  }

  const handleDropdownChange = (selectedCode: string) => {
    const option = FRDropDownOptions.find(option => option.FundingRequestCode === selectedCode)
    if (option) {
      setSelectedFRID(option.FundingRequestId)
      setSelectedFRCode(option.FundingRequestCode)
    }
  }

  const handleExecSchemeChange = (selectedScheme: string) => {
    const option = execSchemeOptions.find(option => option.ExecutionScheme === selectedScheme)
    if (option) {
      setSelectedExecSchemeId(option.OemsExecSchemeId)
      setSelectedExecScheme(option.ExecutionScheme)
    }
  }

  const handleProceedClick = async (customStatus?: string) => {
    if (!userEmail) {
      setRecalcMessage?.('User email is not available. Please refresh the page.')
      setRecalcMessageColor?.('red')
      return
    }

    setIsMutating(true)
    // if (reconcileIndicator === 1) {
    //   try {
    //     const { data: { notifyCounterParty: { message, status } } } = await notifyCounterpartyMutation({
    //       variables: {
    //         batch_unique_id: batchUniqueId,
    //         updated_by: userEmail,
    //         notifyStatus: 'ASSET-READY-FROM-BEFORE-TRADER-CHG-FOR-RECON'
    //       }
    //     })
    //     setRecalcMessage?.(message)
    //     setRecalcMessageColor?.(status === 'failed' ? 'red' : 'green')
    //   } catch (error) {
    //     setRecalcMessage?.('Failed to notify CounterParty.')
    //     setRecalcMessageColor?.('red')
    //   }
    //   setIsMutating(false)
    // } else {
    //   // Use custom status if provided, otherwise use default logic
    //   const approvalStatus = customStatus ?? (riskOverall ? 'TRADER-APPROVED' : 'TRADER-APPROVED-EXCEPTION')
    //   if (batchUniqueId) {
    //     await amendFRAssetStatus({
    //       amendFRAssetStatusMutation,
    //       batchUniqueId,
    //       approvalStatus,
    //       fundingRequestId: selectedFRID,
    //       processRequested: 'CapMarket',
    //       updatedBy: userEmail,
    //       note: traderNote,
    //       setMessage,
    //       setMessageColor
    //     })
    //   }
    //   setIsMutating(false)
    //   onClose?.()
    // }

    // Use custom status if provided, otherwise use default logic
    const approvalStatus = customStatus ?? (riskOverall ? 'TRADER-APPROVED' : 'TRADER-APPROVED-EXCEPTION')
    if (batchUniqueId) {
      await amendFRAssetStatus({
        amendFRAssetStatusMutation,
        batchUniqueId,
        approvalStatus,
        fundingRequestId: selectedFRID,
        processRequested: 'CapMarket',
        updatedBy: userEmail,
        note: traderNote,
        setMessage,
        setMessageColor
      })
    }
    setIsMutating(false)
    onClose?.()
  }

  const handlePrimaryActionWithValidation = async (customStatus?: string) => {
    if (getPrimaryLabel?.toLowerCase().includes('exception') && !traderNote.trim()) {
      setRecalcMessage('Cannot proceed without notes since there is an exception. Please leave a note.')
      setRecalcMessageColor('yellow')
      return
    }
    if (ableToProceed) {
      await handleProceedClick(customStatus)
    } else {
      handleSaveRecalc()
    }
  }

  const handleCancelClick = async () => {
    handleCloseIconClick()
  }

  useEffect(() => {
    if (assetDetailsData?.getRawAssetDetailsByBatchId?.availFRs) {
      const options = assetDetailsData.getRawAssetDetailsByBatchId.availFRs.map((fr: FrOption) => ({
        frID: fr.FundingRequestId,
        frCode: fr.FundingRequestCode,
        selected: fr.selectedFR
      }))

      setFRDropDownOptions(options)

      const selectedFR = options.find((option: FrOption) => option.selectedFR)
      if (selectedFR) {
        setSelectedFRID(selectedFR.frID)
        setSelectedFRCode(selectedFR.frCode)
      }
    }
  }, [assetDetailsData])

  useEffect(() => {
    const handlePostSaveOperations = async () => {
      if (!isMutating) {
        await assetDetailsRefetch()
        if (isConfirmationDialogActive) {
          setIsConfirmationDialogActive(false)
        }
      }
    }

    handlePostSaveOperations()
  }, [isMutating, assetDetailsRefetch, onClose])

  useEffect(() => {
    if (assetDetailsData?.getRawAssetDetailsByBatchId) {
      const assetData = assetDetailsData.getRawAssetDetailsByBatchId
      const transformedData = transformAssetData(assetData.detailsData)
      const inputFieldData = formInputSessionData(assetData.summaryData)
      const summaryData = assetData.summaryData

      let newFRId = null
      let newFRCode = null
      if (assetData.availFRs) {
        const frOptions = assetData.availFRs.map((fr: FrOption) => ({
          FundingRequestId: fr.FundingRequestId,
          FundingRequestCode: fr.FundingRequestCode,
          selectedFR: fr.selectedFR
        }))
        setFRDropDownOptions(frOptions)

        const selectedFR = frOptions.find((fr: FrOption) => fr.selectedFR)
        if (selectedFR) {
          newFRId = selectedFR.FundingRequestId
          newFRCode = selectedFR.FundingRequestCode
          setSelectedFRID(newFRId)
          setSelectedFRCode(newFRCode)
        }
      }

      let newExecSchemeId = null
      let newExecScheme = null
      if (assetData.availOEMSExecScheme) {
        const execOptions = assetData.availOEMSExecScheme.map((scheme: ExecSchemeOption) => ({
          OemsExecSchemeId: scheme.OemsExecSchemeId,
          ExecutionScheme: scheme.ExecutionScheme,
          currSelectedExecScheme: scheme.currSelectedExecScheme
        }))
        setExecSchemeOptions(execOptions)

        const selectedScheme = execOptions.find((scheme: ExecSchemeOption) => scheme.currSelectedExecScheme)
        if (selectedScheme) {
          newExecSchemeId = selectedScheme.OemsExecSchemeId
          newExecScheme = selectedScheme.ExecutionScheme
          setSelectedExecSchemeId(newExecSchemeId)
          setSelectedExecScheme(newExecScheme)
        }
      }

      setDisplayingNotes(assetData.noteData)
      setIsDataReady(summaryData.MarketDataComplete && summaryData.OmsDataComplete && summaryData.RiskCalcComplete)
      setPortfolioReadOnly(!!summaryData.PortfolioReadOnly)
      setReconcileStatus(summaryData.ReconcileStatus ?? null)
      setHolidayValidationMode(summaryData.IsHolidayErrorOrWarning ?? '')
      setBasketInstrumentCurrency(summaryData.BasketInstrumentReportingCurrency ?? '')
      setBasketInstrumentIssue(summaryData.BasketInstrumentIssue ?? '')
      setRecords(transformedData)
      setInputData(inputFieldData)

      // Always refresh the original snapshot after data load/refetch
      originalDataRef.current = {
        originalRecords: transformedData,
        originalInputData: inputFieldData,
        originalFRCode: newFRCode,
        originalFRId: newFRId,
        originalExecSchemeId: newExecSchemeId,
        originalExecScheme: newExecScheme
      }
    }
  }, [assetDetailsData, error, updateError])

  useEffect(() => {
    if (!originalDataRef.current) return

    const recordsChanged = hasRecordDataChanged()
    const formDataChanged = hasInputDataChanged()
    const fundingRequestChanged = hasFundingRequestChanged()
    const execSchemeChanged = hasExecutionSchemeChanged()
    const fieldsFilled = areAllFieldsFilled()
    const dataChanged = recordsChanged || formDataChanged || fundingRequestChanged || execSchemeChanged
    const noteChanged = !!traderNote.trim()

    setAbleToProceed(!recordsChanged && !formDataChanged && !fundingRequestChanged && !execSchemeChanged && fieldsFilled && isDataReady && !hasFlag)
    if (dataChanged) {
      setChangeType('data')
    } else if (noteChanged) {
      setChangeType('note')
    } else {
      setChangeType('none')
    }
    setHasDataChanges(dataChanged)
    setDataChanged(recordsChanged || formDataChanged || fundingRequestChanged || execSchemeChanged || !!traderNote.trim())
  }, [records, inputData, selectedFRCode, selectedExecSchemeId, hasFlag, traderNote])

  useEffect(() => {
    if ((reconcileIndicator === 1 || reconcileIndicator === 4 || reconcileIndicator === 6 || reconcileIndicator === 7 || reconcileIndicator === 8 || reconcileIndicator === 9) && reconcileCount > 0) {
      fetchReconciliationData()
    }
  }, [reconcileIndicator, reconcileCount, batchId, fetchReconciliationData])

  useEffect(() => {
    if (reconciliationData) {
      setReconciliationResultData(reconciliationData?.getReconcileResultById || [])
    }
  }, [reconciliationData, errorRecon])

  return {
    records,
    setRecords,
    inputData,
    setInputData,
    isDataReady,
    loading,
    error,
    QueryWithExtendedTimeComponent,
    isConfirmationDialogActive,
    setIsConfirmationDialogActive,
    isRemoveRecordModalOpen,
    setIsRemoveRecordModalOpen,
    handleRecordRemoval,
    confirmRecordRemoval,
    dataChanged,
    ableToProceed,
    isMutating,
    isSecondaryMutating,
    FRDropDownOptions,
    execSchemeOptions,
    selectedExecScheme,
    selectedFRCode,
    originalFRCode: originalDataRef?.current?.originalFRCode,
    originalExecScheme: originalDataRef.current?.originalExecScheme,
    editableField,
    handleDiscard,
    handleSaveExit,
    handlePrimaryActionWithValidation,
    handleCloseIconClick,
    handleDropdownChange,
    handleExecSchemeChange,
    handleCancelClick,
    getPrimaryLabel,
    portfolioReadOnly,
    shouldShowPreApproveButtons,
    shouldShowPreApproveException,
    shouldShowRejectButton,
    recalcMessage,
    recalcMessageColor,
    setRecalcMessage,
    setRecalcMessageColor,
    traderNote,
    setTraderNote,
    displayingNotes,
    setHasFlag,
    reconciliationResultData,
    loadingReconciliation: loadingRecon,
    errorReconciliation: errorRecon,
    handleRejectClick,
    riskOverall,
    handleSaveRecalc,
    reconcileStatus,
    hasDataChanges,
    holidayAsWarning,
    dateException,
    setDateException,
    basketInstrumentCurrency,
    basketInstrumentIssue
  }
}
